/**
 * VAT rates for the 27 EU member states, for the OSS return.
 *
 * Verified on 2026-08-29 against two independent published tables, which agree
 * on all 27 standard rates. The one correction that pass produced: Estonia,
 * which raised its standard rate from 22% to 24% on 1 July 2025 and made the
 * rise permanent - the figure this site had published was from before that.
 *
 * `reduced` is the rate a webshop most commonly meets, not the only one. Several
 * states operate two or three reduced rates and published tables disagree about
 * which to call the main one, so the tool shows it as a choice and says out loud
 * that the applicable rate depends on the product category. The standard rate is
 * the safe default and the one distance selling usually lands on.
 */
export interface EuVatRate {
  /** ISO 3166-1 alpha-2. */
  code: string;
  standard: number;
  /** The commonly applied reduced rate; null where the state has none. */
  reduced: number | null;
}

export const RATES_VERIFIED_ON = '2026-08-29';

/** Union-wide distance selling threshold, below which the origin rate may still apply. */
export const OSS_THRESHOLD_EUR = 10_000;

export const EU_VAT_RATES: EuVatRate[] = [
  { code: 'AT', standard: 20, reduced: 10 },
  { code: 'BE', standard: 21, reduced: 6 },
  { code: 'BG', standard: 20, reduced: 9 },
  { code: 'CY', standard: 19, reduced: 9 },
  { code: 'CZ', standard: 21, reduced: 12 },
  { code: 'DE', standard: 19, reduced: 7 },
  { code: 'DK', standard: 25, reduced: null },
  { code: 'EE', standard: 24, reduced: 9 },
  { code: 'ES', standard: 21, reduced: 10 },
  { code: 'FI', standard: 25.5, reduced: 14 },
  { code: 'FR', standard: 20, reduced: 10 },
  { code: 'GR', standard: 24, reduced: 13 },
  { code: 'HR', standard: 25, reduced: 13 },
  { code: 'HU', standard: 27, reduced: 18 },
  { code: 'IE', standard: 23, reduced: 13.5 },
  { code: 'IT', standard: 22, reduced: 10 },
  { code: 'LT', standard: 21, reduced: 9 },
  { code: 'LU', standard: 17, reduced: 14 },
  { code: 'LV', standard: 21, reduced: 12 },
  { code: 'MT', standard: 18, reduced: 7 },
  { code: 'NL', standard: 21, reduced: 9 },
  { code: 'PL', standard: 23, reduced: 8 },
  { code: 'PT', standard: 23, reduced: 13 },
  { code: 'RO', standard: 21, reduced: 11 },
  { code: 'SE', standard: 25, reduced: 12 },
  { code: 'SI', standard: 22, reduced: 9.5 },
  { code: 'SK', standard: 23, reduced: 19 },
];

export function euVatRate(code: string): EuVatRate | undefined {
  return EU_VAT_RATES.find((r) => r.code === code.toUpperCase());
}

/**
 * The country's name in the reader's language.
 *
 * Intl carries all 27 names in every locale the site serves, so there is no
 * hand-maintained translation table here to drift. Falls back to the code if a
 * runtime somehow ships without the region data.
 */
export function countryName(code: string, locale: string): string {
  try {
    return new Intl.DisplayNames([locale], { type: 'region' }).of(code) ?? code;
  } catch {
    return code;
  }
}

/** VAT due on a net amount, rounded to cents. */
export function vatOn(netAmount: number, ratePercent: number): number {
  return Math.round(netAmount * ratePercent) / 100;
}
